import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  PhoneCall, MessageSquare, CalendarPlus, UserPlus, CheckCircle2, XCircle, History,
} from "lucide-react";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { SmsDialog } from "./SmsDialog";
import {
  agreementLink, buildSchedulingLinkSms, firstName, fmtDate, smsHref,
} from "./leadUtils";
import { useConfig } from "../../hooks/useConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActionBtn = ({ onClick, href, icon: Icon, children, tone = "zinc", testid }) => {
  const tones = {
    zinc: "border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white",
    blue: "border-blue-500/50 text-blue-400 hover:bg-blue-500/10",
    green: "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10",
    red: "border-red-500/50 text-red-400 hover:bg-red-500/10",
  };
  const cls = `inline-flex items-center gap-1.5 border rounded-sm px-3 h-9 text-xs font-semibold transition-colors ${tones[tone]}`;
  if (href) return <a data-testid={testid} href={href} className={cls}><Icon className="h-3.5 w-3.5" />{children}</a>;
  return <button data-testid={testid} onClick={onClick} className={cls}><Icon className="h-3.5 w-3.5" />{children}</button>;
};

export const LeadManageDialog = ({ lead, onClose, onUpdated, onSchedule }) => {
  const config = useConfig();
  const [note, setNote] = useState("");
  const [smsOpen, setSmsOpen] = useState(false);

  if (!lead) return null;

  const patch = async (updates, msg) => {
    try {
      const res = await axios.patch(`${API}/leads/${lead.id}`, updates);
      onUpdated(res.data);
      toast.success(msg);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    const stamp = new Date().toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    const combined = lead.notes ? `${lead.notes}\n[${stamp}] ${note.trim()}` : `[${stamp}] ${note.trim()}`;
    await patch({ notes: combined }, "Note saved");
    setNote("");
  };

  const convert = async () => {
    try {
      const res = await axios.post(`${API}/admin/leads/${lead.id}/convert`);
      onUpdated({ ...lead, status: "Converted to Customer", converted_customer_id: res.data.customer.customer_id });
      toast.success(res.data.existing
        ? "Matched to an existing customer (no duplicate created) — see Customers tab"
        : "Lead converted to customer — see Customers tab");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Conversion failed");
    }
  };

  const logSms = async (message) => {
    try {
      await axios.post(`${API}/admin/leads/${lead.id}/contact-log`, { message, channel: "sms" });
      onUpdated({
        ...lead,
        contact_history: [...(lead.contact_history || []), { message, channel: "sms", sent_at: new Date().toISOString() }],
      });
      toast.success("Message logged to contact history");
    } catch {
      toast.error("Could not log message");
    }
  };

  const schedulingMsg = buildSchedulingLinkSms(
    firstName(lead.name),
    config.booking_url || "(booking link not configured)",
    agreementLink(lead.id),
  );

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lead-manage-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{lead.name}</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-zinc-400 -mt-2 space-y-0.5">
          <div>{lead.phone}{lead.email ? ` · ${lead.email}` : ""}</div>
          <div>{lead.vehicle_year} {lead.vehicle_make} {lead.vehicle_model} — {lead.service_needed}</div>
          {lead.referral_source && <div className="text-zinc-500 text-xs">Heard about us: {lead.referral_source}</div>}
          <div className="text-zinc-600 text-xs">Created {fmtDate(lead.created_at)} · Status: {lead.status}</div>
        </div>

        {lead.message && (
          <div className="border-t border-zinc-800 pt-4">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Customer Message</div>
            <p data-testid="lead-manage-message" className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap bg-[#0a0a0a] border border-zinc-800 rounded-sm p-3">{lead.message}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 border-t border-zinc-800 pt-4">
          <ActionBtn testid="lead-action-call" href={`tel:${lead.phone.replace(/[^+\d]/g, "")}`} icon={PhoneCall} tone="blue">Call</ActionBtn>
          <ActionBtn testid="lead-action-text" href={smsHref(lead.phone, `Hi ${firstName(lead.name)}, this is Mason's Mobile Mechanic. `)} icon={MessageSquare} tone="blue">Text</ActionBtn>
          <ActionBtn testid="lead-action-contacted" onClick={() => patch({ status: "Contacted" }, "Marked as contacted")} icon={CheckCircle2} tone="green">Mark Contacted</ActionBtn>
          <ActionBtn testid="lead-action-schedule" onClick={() => onSchedule(lead)} icon={CalendarPlus} tone="blue">Schedule Appointment</ActionBtn>
          <ActionBtn testid="lead-action-send-link" onClick={() => setSmsOpen(true)} icon={MessageSquare} tone="blue">Send Scheduling Link</ActionBtn>
          {lead.status !== "Converted to Customer" && (
            <ActionBtn testid="lead-action-convert" onClick={convert} icon={UserPlus} tone="green">Convert to Customer</ActionBtn>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <ActionBtn testid="lead-action-declined" onClick={() => patch({ status: "Declined" }, "Marked declined — lead kept for reference")} icon={XCircle} tone="red">Declined Service</ActionBtn>
          <ActionBtn testid="lead-action-close" onClick={() => patch({ status: "Closed" }, "Opportunity completed & closed")} icon={CheckCircle2}>Complete Opportunity</ActionBtn>
        </div>

        <div className="border-t border-zinc-800 pt-4 space-y-2">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Add Note</div>
          <Textarea
            data-testid="lead-note-input"
            className="bg-[#0a0a0a] border-zinc-800 text-white rounded-sm h-20 text-sm"
            placeholder="e.g. Called — declined service, price shopper. Keep for future reference."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button
            data-testid="lead-note-save"
            onClick={saveNote}
            className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 text-white rounded-sm px-4 py-2 text-xs font-semibold transition-colors"
          >
            Save Note
          </button>
          {lead.notes && (
            <pre className="text-zinc-400 text-xs whitespace-pre-wrap bg-[#0a0a0a] border border-zinc-800 rounded-sm p-3 max-h-32 overflow-y-auto">{lead.notes}</pre>
          )}
        </div>

        <div className="border-t border-zinc-800 pt-4">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 flex items-center gap-2">
            <History className="h-3.5 w-3.5" /> Contact History
          </div>
          {(lead.contact_history || []).length === 0 ? (
            <p className="text-zinc-600 text-xs">No messages logged yet.</p>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {[...lead.contact_history].reverse().map((h, i) => (
                <div key={`${h.sent_at}-${i}`} className="bg-[#0a0a0a] border border-zinc-800 rounded-sm p-2.5">
                  <div className="text-zinc-600 text-[10px] uppercase mb-1">{h.channel} · {fmtDate(h.sent_at)}</div>
                  <div className="text-zinc-300 text-xs">{h.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {smsOpen && (
          <SmsDialog
            key={`link-${lead.id}`}
            open={smsOpen}
            onOpenChange={setSmsOpen}
            phone={lead.phone}
            title="Send Scheduling Link"
            initialMessage={schedulingMsg}
            onLogged={logSms}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
